import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ASSETS } from "../lib/assets";
import { useReveal } from "../lib/hooks";
import { resolveImageUrl } from "../lib/adminApi";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Cuaderno() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    axios
      .get(`${API}/journal`)
      .then((r) => mounted && setEntries(r.data))
      .catch(() => mounted && setEntries([]))
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, []);

  return (
    <main data-testid="cuaderno-page" className="bg-[var(--pc-bg)]">
      <CuadernoHero />

      <section
        data-testid="journal-list"
        className="border-t border-[var(--pc-line)]"
      >
        {loading && (
          <div className="pc-container py-24 label-small">Cargando notas...</div>
        )}
        {!loading && entries.length === 0 && (
          <div className="pc-container py-24 label-small">
            Pronto publicaremos nuevas notas.
          </div>
        )}
        {!loading &&
          entries.map((e, idx) => (
            <JournalRow key={e.id} entry={e} isLast={idx === entries.length - 1} />
          ))}
      </section>
    </main>
  );
}

/* ============================== HERO ============================== */
function CuadernoHero() {
  const head = useReveal();
  const body = useReveal();
  const img = useReveal({ threshold: 0.1 });

  return (
    <section
      data-testid="cuaderno-hero"
      className="relative w-full overflow-hidden"
      style={{ minHeight: "78vh" }}
    >
      {/* Full-bleed sketchbook image with black fade on the left */}
      <div
        ref={img.ref}
        className={`absolute inset-0 reveal-img ${img.revealed ? "is-in" : ""}`}
      >
        <img
          src={ASSETS.cuadernoHero}
          alt="Cuaderno de notas de arquitectura"
          className="absolute inset-0 w-full h-full object-cover"
          draggable="false"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(90deg, rgba(7,7,7,0.97) 0%, rgba(7,7,7,0.92) 24%, rgba(7,7,7,0.62) 42%, rgba(7,7,7,0.22) 60%, rgba(7,7,7,0) 80%)",
          }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(7,7,7,0.35) 0%, transparent 16%, transparent 80%, rgba(7,7,7,0.96) 100%)",
          }}
        />
      </div>

      <div
        className="relative z-10 pc-container flex flex-col justify-center"
        style={{ paddingTop: "190px", paddingBottom: "80px", minHeight: "78vh" }}
      >
        <div className="max-w-[560px]">
          <div ref={head.ref} className={`reveal ${head.revealed ? "is-in" : ""}`}>
            <h1
              className="headline-xl"
              style={{
                fontSize: "clamp(31px, 3.7vw, 55px)",
                letterSpacing: "0.18em",
                lineHeight: 1.14,
                maxWidth: 500,
              }}
            >
              Piensa antes<br />de construir.
            </h1>
            <div className="h-px w-12 bg-[var(--pc-bronze)] mt-8 opacity-90" />
          </div>
          <p
            ref={body.ref}
            className={`body-soft mt-10 max-w-[420px] reveal delay-1 ${body.revealed ? "is-in" : ""}`}
            style={{ fontSize: 16.5 }}
          >
            Lee las notas de Paco Cabrera sobre arquitectura, materia,
            territorio y permanencia. Tómate tu tiempo.
          </p>
        </div>
      </div>
    </section>
  );
}

/* ============================== ROW ============================== */
function JournalRow({ entry, isLast }) {
  const row = useReveal({ threshold: 0.18 });

  return (
    <Link
      to={`/cuaderno/${entry.id}`}
      data-testid={`journal-entry-${entry.id}`}
      className={`block group ${!isLast ? "border-b border-[var(--pc-line)]" : ""} hover:bg-[#0d0d0f] transition-colors duration-700`}
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 items-center gap-0">
        {/* Number */}
        <div
          ref={row.ref}
          className={`lg:col-span-1 px-6 md:px-10 lg:px-14 py-10 lg:py-14 reveal ${row.revealed ? "is-in" : ""}`}
        >
          <div
            className="text-[var(--pc-text)]"
            style={{
              fontFamily: "Inter, sans-serif",
              fontWeight: 200,
              fontSize: "clamp(40px, 4vw, 60px)",
              lineHeight: 1,
              letterSpacing: "0.04em",
            }}
          >
            {entry.number}
          </div>
          <div className="h-px w-7 bg-[var(--pc-bronze)] mt-4 opacity-90 group-hover:w-12 transition-all duration-500" />
        </div>

        {/* Text block */}
        <div
          className={`lg:col-span-6 px-6 md:px-10 lg:px-10 py-6 lg:py-14 reveal delay-1 ${row.revealed ? "is-in" : ""}`}
        >
          <h2
            className="headline-md text-[var(--pc-text)] group-hover:text-[var(--pc-bronze-bright)] transition-colors duration-500"
            style={{
              fontSize: "clamp(20px, 1.8vw, 28px)",
              letterSpacing: "0.12em",
              lineHeight: 1.28,
              whiteSpace: "pre-line",
            }}
          >
            {entry.title}
          </h2>
          <p
            className="body-soft mt-6 max-w-[460px]"
            style={{ fontSize: 14.5 }}
          >
            {entry.excerpt}
          </p>
          <div
            className="mt-8 text-[var(--pc-bronze)]"
            style={{
              fontFamily: "Inter, sans-serif",
              fontWeight: 400,
              fontSize: 11,
              letterSpacing: "0.32em",
              textTransform: "uppercase",
            }}
          >
            {formatDate(entry.date)}
          </div>
        </div>

        {/* Image */}
        <div
          className={`lg:col-span-5 relative min-h-[220px] lg:min-h-[260px] img-wrap reveal-img ${row.revealed ? "is-in" : ""}`}
        >
          <img
            src={resolveImageUrl(entry.image)}
            alt={entry.title.replace("\n", " ")}
            className="absolute inset-0 w-full h-full object-cover"
            draggable="false"
          />
        </div>
      </div>
    </Link>
  );
}

function formatDate(iso) {
  try {
    const [y, m, d] = iso.split("-");
    const months = [
      "ENE", "FEB", "MAR", "ABR", "MAY", "JUN",
      "JUL", "AGO", "SEP", "OCT", "NOV", "DIC",
    ];
    return `${d} ${months[parseInt(m, 10) - 1]} ${y}`;
  } catch {
    return iso;
  }
}
